
import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Template } from "@/lib/types";
import { fetchTemplates } from "@/services/templateService";
import { Skeleton } from "@/components/ui/skeleton";
import TemplateItem from "./TemplateItem";
import EmptyTemplateState from "./EmptyTemplateState";
import TemplateDrawer from "./TemplateDrawer";
import DeleteTemplateDialog from "./DeleteTemplateDialog";

interface TemplatesListProps {
  tenantId: string;
  onCreateTemplate: () => void;
  refreshTrigger?: number; // Optional refresh trigger
}

const TemplatesList: React.FC<TemplatesListProps> = ({ 
  tenantId, 
  onCreateTemplate,
  refreshTrigger = 0 
}) => {
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["templates", tenantId, refreshTrigger],
    queryFn: () => fetchTemplates(tenantId),
  });

  const handleEditTemplate = (template: Template) => {
    setSelectedTemplate(template);
    setEditDrawerOpen(true);
  };

  const handleDeleteTemplate = (template: Template) => {
    setSelectedTemplate(template);
    setDeleteDialogOpen(true);
  };

  if (isLoading) {
    return (
      <div className="rounded-md border">
        <div className="grid grid-cols-4 border-b px-4 py-3 font-medium">
          <div>Name</div>
          <div>Last Modified</div>
          <div>Usage Count</div>
          <div>Actions</div>
        </div>
        {[...Array(3)].map((_, i) => (
          <div key={i} className="grid grid-cols-4 items-center py-4 px-4">
            <Skeleton className="h-4 w-[150px]" />
            <Skeleton className="h-4 w-[100px]" />
            <Skeleton className="h-4 w-[60px]" />
            <div className="flex justify-end gap-2">
              <Skeleton className="h-8 w-8 rounded-md" />
              <Skeleton className="h-8 w-8 rounded-md" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="rounded-md border p-4 text-center text-red-500">
        Error loading templates. Please try again later.
      </div>
    );
  }

  if (!data?.length) {
    return (
      <div className="rounded-md border">
        <EmptyTemplateState onCreateTemplate={onCreateTemplate} />
      </div>
    );
  }

  return (
    <>
      <div className="rounded-md border">
        <div className="grid grid-cols-4 border-b px-4 py-3 font-medium">
          <div>Name</div>
          <div>Last Modified</div>
          <div>Usage Count</div>
          <div className="text-right">Actions</div>
        </div>
        {data.map((template) => (
          <TemplateItem
            key={template.id}
            template={template}
            onEdit={handleEditTemplate}
            onDelete={handleDeleteTemplate}
          />
        ))}
      </div>
      
      <TemplateDrawer
        tenantId={tenantId}
        template={selectedTemplate}
        mode="edit"
        open={editDrawerOpen}
        onOpenChange={setEditDrawerOpen}
        onSuccess={refetch}
        triggerButton={false}
      />
      
      <DeleteTemplateDialog
        template={selectedTemplate}
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onSuccess={refetch}
      />
    </>
  );
};

export default TemplatesList;
